import { useDebounce } from '@/hooks/useDebounce';
import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Eye, Archive, FileText, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useDownloads, useDeleteDownload } from '@/queries/downloads.queries';
import { useAppSelector } from '@/store/hooks';
import { Card, Badge, Spinner } from '@/components/ui';
import { PreviewModal } from '@/pages/StandardizerPage';

interface DownloadFile {
  id: string;
  owner_id: string;
  filename: string;
  byte_size: number;
  content_type: string;
  created_at: string;
  file_path: string;
  user_role: string | null;
  owner_name?: string;
  archived?: boolean;
}

function getFormatBadgeTone(filename: string): 'accent' | 'primary' | 'muted' | 'ok' | 'warn' | 'danger' {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext === 'html') return 'ok';
  if (ext === 'docx') return 'primary';
  if (ext === 'pptx') return 'warn';
  if (ext === 'pdf') return 'danger';
  return 'muted';
}

function getFriendlyType(contentType: string, filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  if (contentType.includes('html') || ext === 'html') return 'HTML Page';
  if (contentType.includes('word') || ext === 'docx') return 'Word Document';
  if (contentType.includes('presentation') || ext === 'pptx') return 'PowerPoint Presentation';
  if (contentType.includes('pdf') || ext === 'pdf') return 'PDF Document';
  if (contentType.includes('markdown') || ext === 'md') return 'Markdown Document';
  return contentType || 'Document';
}

export function DownloadsPage() {
  const currentUser = useAppSelector((state) => state.auth.user);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'non-archived' | 'archived' | 'all'>('non-archived');
  const [fileTypeFilter, setFileTypeFilter] = useState<'all' | 'docx' | 'pptx' | 'pdf' | 'html' | 'md'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const debouncedSearch = useDebounce(searchQuery, 500);

  const { data: response, isLoading, error } = useDownloads({
    search: debouncedSearch,
    status: statusFilter,
    format: fileTypeFilter,
    page: currentPage,
    limit: itemsPerPage,
  });

  const deleteMutation = useDeleteDownload();
  const [previewFile, setPreviewFile] = useState<DownloadFile | null>(null);

  const isOwner = currentUser?.permissions?.includes('audit.view') || false; // Owner role has audit.view permission

  const rawDownloads = response?.data || [];
  const totalItems = response?.total || 0;
  const totalPages = Math.max(1, response?.totalPages || 1);

  // Reset to page 1 whenever any filter or itemsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, fileTypeFilter, itemsPerPage]);

  const formattedDownloads = useMemo(() => {
    return rawDownloads.map((d) => ({
      ...d,
      formattedDate: new Date(d.created_at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      friendlySize: `${Math.max(1, Math.round(d.byte_size / 1024))} KB`,
      friendlyType: getFriendlyType(d.content_type, d.filename),
      format: d.filename.split('.').pop()?.toUpperCase() || 'FILE',
    }));
  }, [rawDownloads]);

  const [archiveModal, setArchiveModal] = useState<{ isOpen: boolean; fileId: string | null; filename: string | null }>({
    isOpen: false,
    fileId: null,
    filename: null,
  });

  const handleOpenArchiveModal = (item: DownloadFile) => {
    setArchiveModal({
      isOpen: true,
      fileId: item.id,
      filename: item.filename,
    });
  };

  const handleConfirmArchive = () => {
    if (archiveModal.fileId) {
      deleteMutation.mutate(archiveModal.fileId);
    }
    setArchiveModal({ isOpen: false, fileId: null, filename: null });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Downloads</h1>
          {isOwner && (
            <Badge tone="danger" className="text-xs">
              Owner Mode
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {isOwner ? 'Showing all employee downloads history' : 'Your personal download history'}
        </p>
      </div>

      {/* Search, Status, and File Format Radio Filter Toolbar */}
      <div className="bg-card border border-border rounded-xl p-4 space-y-4 shadow-sm">
        {/* Top Controls Row: Search Bar (takes majority width) + Status Dropdown */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                isOwner
                  ? "Search by Title, Uploaded By, file type or Date..."
                  : "Search by Title, file type or Date..."
              }
              className="w-full pl-9 pr-8 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground placeholder:text-muted-foreground shadow-sm transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground font-semibold"
              >
                ✕
              </button>
            )}
          </div>

          {/* Status Filter Dropdown */}
          <div className="flex items-center gap-2 shrink-0">
            <label htmlFor="status-filter-select" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider shrink-0">
              Status:
            </label>
            <select
              id="status-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer shadow-sm min-w-[130px]"
            >
              <option value="non-archived">Non-Archived</option>
              <option value="archived">Archived</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        {/* Bottom Options Row: File Type Radio Pills */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-2 border-t border-border/50">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider shrink-0">
            Format:
          </span>
          <div className="flex flex-wrap items-center gap-1.5 w-full sm:w-auto">
            {[
              { label: 'All', value: 'all' },
              { label: 'Word (.docx)', value: 'docx' },
              { label: 'PPT (.pptx)', value: 'pptx' },
              { label: 'PDF (.pdf)', value: 'pdf' },
              { label: 'HTML (.html)', value: 'html' },
              { label: 'Markdown (.md)', value: 'md' },
            ].map((option) => {
              const isSelected = fileTypeFilter === option.value;
              return (
                <label
                  key={option.value}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition-all flex items-center gap-1.5 border ${isSelected
                    ? 'bg-primary text-primary-foreground border-primary shadow-xs font-semibold'
                    : 'bg-background hover:bg-muted text-muted-foreground border-border'
                    }`}
                >
                  <input
                    type="radio"
                    name="file-type-filter"
                    value={option.value}
                    checked={isSelected}
                    onChange={() => setFileTypeFilter(option.value as any)}
                    className="sr-only"
                  />
                  <span>{option.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground gap-2">
          <Spinner className="h-5 w-5" />
          Loading downloads...
        </div>
      ) : error ? (
        <div className="card p-8 text-center text-destructive">
          Failed to load downloads history. Please try again.
        </div>
      ) : formattedDownloads.length === 0 ? (
        <div className="card p-12 text-center text-muted-foreground space-y-2">
          <Search className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
          <h3 className="text-base font-semibold text-foreground">No matching records found.</h3>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            Try adjusting your search query or switching status and format filters.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3">
            {formattedDownloads.map((item) => {
              return (
                <Card key={item.id} className="p-4 flex items-center justify-between hover:border-primary/30 transition-colors">
                  <div className="flex items-center gap-4 min-w-0">
                    {/* Format Badge */}
                    <Badge tone={getFormatBadgeTone(item.filename)} className="w-16 h-8 justify-center shrink-0">
                      {item.format}
                    </Badge>

                    {/* Document Icon */}
                    <FileText className="w-5 h-5 text-muted-foreground shrink-0" />

                    {/* Text details */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="block font-semibold text-foreground truncate text-sm sm:text-base">
                          {item.filename}
                        </span>
                        {isOwner && item.owner_name && (
                          <Badge tone="muted" className="text-[10px] h-5 px-1.5 shrink-0">
                            by {item.owner_name}
                          </Badge>
                        )}
                        {isOwner && item.archived && (
                          <Badge tone="warn" className="text-[10px] h-5 px-1.5 shrink-0 font-semibold">
                            Archived
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground mt-0.5 font-medium">
                        <span>{item.friendlySize}</span>
                        <span>•</span>
                        <span>{item.friendlyType}</span>
                        <span>•</span>
                        <span>{item.formattedDate}</span>
                      </div>
                    </div>
                  </div>

                  {/* Right Actions */}
                  <div className="flex items-center gap-1.5 shrink-0 ml-4">
                    <button
                      onClick={() => setPreviewFile(item)}
                      title="Preview document"
                      className="w-9 h-9 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors border border-border"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleOpenArchiveModal(item)}
                      title="Archive document"
                      className="w-9 h-9 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors border border-border"
                    >
                      <Archive className="w-4 h-4" />
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Pagination Footer Controls */}
          {totalItems > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-3 border-t border-border/50 text-xs text-muted-foreground">
              {/* Left Summary */}
              <div>
                Showing <strong className="text-foreground">{(currentPage - 1) * itemsPerPage + 1}</strong> to{' '}
                <strong className="text-foreground">{Math.min(currentPage * itemsPerPage, totalItems)}</strong> of{' '}
                <strong className="text-foreground">{totalItems}</strong> documents
              </div>

              {/* Right Controls */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Items Per Page Select */}
                <div className="flex items-center gap-1.5">
                  <span>Per page:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    className="px-2 py-1 bg-background border border-border rounded text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 cursor-pointer shadow-xs"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                {/* Previous / Next buttons */}
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    className="p-1.5 rounded border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    title="Previous Page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <span className="font-medium px-2">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    className="p-1.5 rounded border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    title="Next Page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Archive Confirmation Custom Popup Modal */}
      {archiveModal.isOpen && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4 animate-scale-in">
            <div className="flex items-center gap-3 text-amber-500">
              <div className="p-2.5 bg-amber-500/10 rounded-full">
                <Archive className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Archive Document</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Do you want to archive <strong className="text-foreground">{archiveModal.filename}</strong>?
              Once archived, this file will no longer be visible in your downloads list.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setArchiveModal({ isOpen: false, fileId: null, filename: null })}
                className="px-4 py-2 text-xs font-semibold rounded-md border border-border text-foreground hover:bg-muted transition-colors cursor-pointer"
              >
                No, Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmArchive}
                className="px-4 py-2 text-xs font-semibold rounded-md bg-amber-500 text-white hover:bg-amber-600 shadow-sm transition-colors cursor-pointer"
              >
                Yes, Archive
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {previewFile && (
        <PreviewModal
          isOpen={!!previewFile}
          onClose={() => setPreviewFile(null)}
          fileId={previewFile.id}
          filename={previewFile.filename}
          format={previewFile.filename.split('.').pop()?.toLowerCase() || ''}
          canEdit={!isOwner || String(previewFile.owner_id) === String(currentUser?.id)}
        />
      )}
    </div>
  );
}
